/**
 * Web Mercator, which is the projection every slippy map on the internet agrees on.
 *
 * Beeline is a map-free product and stays one — the map exists **only** to choose a point, never
 * to walk to one. But choosing a point needs tiles, tiles need a projection, and both the
 * browser (laying out a tile grid) and the server (validating a tile request before proxying it)
 * need to agree about it exactly. So it lives here, with the rest of the geometry.
 *
 * Tile coordinates are the standard XYZ scheme: at zoom `z` the world is a 2^z × 2^z grid, `x`
 * runs west to east, and `y` runs **north to south** — which is upside down from latitude and is
 * the single most common source of an off-by-one-world bug.
 */

import type { LatLng } from './geo';

/** Every tile is 256 px square. This is not configurable anywhere in the world. */
export const TILE_SIZE = 256;

/**
 * Web Mercator cannot represent the poles, so it stops just short of them. This is the exact
 * cutoff that makes the projected world square, and every tile server uses it.
 */
export const MAX_LATITUDE = 85.0511287798;

/** How many tiles across the world is, at a given zoom. */
export const tilesAcross = (zoom: number): number => 2 ** Math.max(0, Math.floor(zoom));

export const clampLatitude = (lat: number): number =>
  Math.min(MAX_LATITUDE, Math.max(-MAX_LATITUDE, lat));

/** Wrap a longitude into [-180, 180). Panning east past the date line must not fall off. */
export function wrapLongitude(lng: number): number {
  if (!Number.isFinite(lng)) return 0;
  const wrapped = (((lng + 180) % 360) + 360) % 360;
  return wrapped - 180;
}

/** A position in *fractional* tile units at a zoom: the integer part is the tile, the rest is within it. */
export interface TilePoint {
  x: number;
  y: number;
}

/** Project a position onto the tile grid at a zoom. Fractional, so it can place a pixel. */
export function project(position: LatLng, zoom: number): TilePoint {
  const scale = tilesAcross(zoom);
  const lat = clampLatitude(position.lat);
  const sinLat = Math.sin((lat * Math.PI) / 180);

  return {
    x: ((wrapLongitude(position.lng) + 180) / 360) * scale,
    // The log-tangent term is what makes Mercator conformal — and what makes `y` run southward.
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
  };
}

/** The inverse of `project`. Given a point on the tile grid, where on Earth is it? */
export function unproject(point: TilePoint, zoom: number): LatLng {
  const scale = tilesAcross(zoom);
  const n = Math.PI * (1 - (2 * point.y) / scale);

  return {
    lat: (Math.atan(Math.sinh(n)) * 180) / Math.PI,
    lng: wrapLongitude((point.x / scale) * 360 - 180),
  };
}

/**
 * How many metres one screen pixel covers, here, at this zoom.
 *
 * Mercator stretches with latitude, so a scale bar drawn from a constant would be wrong
 * everywhere except the equator. Used to size the accuracy circle honestly.
 */
export function metresPerPixel(latitude: number, zoom: number): number {
  const equatorialCircumference = 40_075_016.686;
  return (
    (equatorialCircumference * Math.cos((clampLatitude(latitude) * Math.PI) / 180)) /
    (TILE_SIZE * tilesAcross(zoom))
  );
}

/** One tile's address. */
export interface TileAddress {
  z: number;
  x: number;
  y: number;
}

/**
 * Is this a tile that can exist?
 *
 * The server asks before proxying anything upstream. Without it, `x` and `y` are just numbers
 * pasted into a URL by a stranger, which is how a tile proxy becomes an open one.
 */
export function isValidTile(tile: TileAddress, maxZoom: number): boolean {
  const { z, x, y } = tile;
  if (!Number.isInteger(z) || !Number.isInteger(x) || !Number.isInteger(y)) return false;
  if (z < 0 || z > maxZoom) return false;

  const across = tilesAcross(z);
  // `y` is never wrapped: there is no tile above the north pole. `x` is not wrapped here either,
  // because the caller has already normalised it — an out-of-range x is a malformed request.
  return x >= 0 && x < across && y >= 0 && y < across;
}

/** Wrap a tile column around the date line, so panning east forever keeps working. */
export const wrapTileX = (x: number, zoom: number): number => {
  const across = tilesAcross(zoom);
  return ((Math.floor(x) % across) + across) % across;
};
